"use client";

import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 py-12">
        {/* Back Button */}
        <Link
          href="/paywall"
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white via-orange-200 to-orange-400 bg-clip-text text-transparent">
            Terms and Conditions
          </h1>
          <p className="text-gray-400">Last updated: 10/17/2025</p>
        </motion.div>

        {/* Table of Contents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12 p-6 bg-gray-800/30 rounded-xl border border-gray-700/50"
        >
          <h2 className="text-xl font-bold mb-4">Table of Contents</h2>
          <ol className="space-y-2 text-gray-300">
            <li>
              <a href="#introduction" className="hover:text-orange-400 transition-colors">
                1. Introduction
              </a>
            </li>
            <li>
              <a href="#definitions" className="hover:text-orange-400 transition-colors">
                2. Definitions
              </a>
            </li>
            <li>
              <a href="#acceptance" className="hover:text-orange-400 transition-colors">
                3. Acceptance of Terms
              </a>
            </li>
            <li>
              <a href="#service" className="hover:text-orange-400 transition-colors">
                4. Service Description
              </a>
            </li>
            <li>
              <a href="#accounts" className="hover:text-orange-400 transition-colors">
                5. User Accounts
              </a>
            </li>
            <li>
              <a href="#payment" className="hover:text-orange-400 transition-colors">
                6. Subscription & Payment
              </a>
            </li>
            <li>
              <a href="#limitations" className="hover:text-orange-400 transition-colors">
                7. AI Service Limitations
              </a>
            </li>
            <li>
              <a href="#privacy" className="hover:text-orange-400 transition-colors">
                8. Data Collection & Storage
              </a>
            </li>
            <li>
              <a href="#voice" className="hover:text-orange-400 transition-colors">
                9. Voice Data Processing
              </a>
            </li>
            <li>
              <a href="#analytics" className="hover:text-orange-400 transition-colors">
                10. Analytics & Tracking
              </a>
            </li>
            <li>
              <a href="#third-party" className="hover:text-orange-400 transition-colors">
                11. Third-Party Services
              </a>
            </li>
            <li>
              <a href="#conduct" className="hover:text-orange-400 transition-colors">
                12. User Conduct
              </a>
            </li>
            <li>
              <a href="#ip" className="hover:text-orange-400 transition-colors">
                13. Intellectual Property
              </a>
            </li>
            <li>
              <a href="#disclaimers" className="hover:text-orange-400 transition-colors">
                14. Disclaimers
              </a>
            </li>
            <li>
              <a href="#liability" className="hover:text-orange-400 transition-colors">
                15. Limitation of Liability
              </a>
            </li>
            <li>
              <a href="#termination" className="hover:text-orange-400 transition-colors">
                16. Termination
              </a>
            </li>
            <li>
              <a href="#governing" className="hover:text-orange-400 transition-colors">
                17. Governing Law
              </a>
            </li>
            <li>
              <a href="#changes" className="hover:text-orange-400 transition-colors">
                18. Changes to Terms
              </a>
            </li>
            <li>
              <a href="#contact" className="hover:text-orange-400 transition-colors">
                19. Contact Information
              </a>
            </li>
          </ol>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-invert prose-orange max-w-none"
        >
          {/* Section 1 */}
          <section id="introduction" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Welcome to Chicky, an AI-powered language tutoring service operated by Chicky. These
              Terms and Conditions ("Terms") govern your use of our AI voice agent service that
              provides personalized language learning experiences with memory capabilities.
            </p>
            <p className="text-gray-300 leading-relaxed">
              By accessing or using Chicky, you agree to be bound by these Terms and our Privacy
              Policy. If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          {/* Section 2 */}
          <section id="definitions" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">2. Definitions</h2>
            <ul className="space-y-2 text-gray-300">
              <li>
                <strong>"Service"</strong> refers to the Chicky AI language tutoring platform and
                all related features.
              </li>
              <li>
                <strong>"AI Agent"</strong> refers to our artificial intelligence voice tutor that
                provides language learning assistance.
              </li>
              <li>
                <strong>"User"</strong> refers to any individual who accesses or uses our Service.
              </li>
              <li>
                <strong>"Subscription"</strong> refers to paid access to premium features of our
                Service.
              </li>
              <li>
                <strong>"Content"</strong> refers to all information, data, text, audio, and other
                materials generated or shared through the Service.
              </li>
              <li>
                <strong>"Personal Data"</strong> has the meaning given to it under the General Data
                Protection Regulation (GDPR).
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section id="acceptance" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">3. Acceptance of Terms</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              By creating an account or using Chicky, you confirm that:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li>
                You are at least 16 years old, or have parental consent if you are between 13-16
                years old
              </li>
              <li>You have the legal capacity to enter into these Terms</li>
              <li>You will comply with all applicable laws and regulations</li>
              <li>All information you provide is accurate and truthful</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section id="service" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">4. Service Description</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Chicky provides an AI-powered language learning experience featuring:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li>Interactive voice conversations with our AI tutor</li>
              <li>Personalized learning paths based on your progress</li>
              <li>
                Memory capabilities that remember your previous conversations and learning history
              </li>
              <li>Real-time feedback and pronunciation assistance</li>
              <li>Progress tracking and analytics</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              Our Service is designed to supplement, not replace, formal language education. While
              our AI is sophisticated, it may occasionally provide imperfect responses or
              recommendations.
            </p>
          </section>

          {/* Section 5 */}
          <section id="accounts" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">5. User Accounts</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              To access certain features, you must create an account. You are responsible for:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Immediately notifying us of any unauthorized use</li>
              <li>Providing accurate and current information</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              You may not share your account or allow others to use your account. We reserve the
              right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          {/* Section 6 */}
          <section id="payment" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">6. Subscription & Payment</h2>
            <div className="space-y-4 text-gray-300">
              <div>
                <p className="font-bold mb-2">Subscription Tiers:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Free Tier:</strong> Limited access to basic features with usage
                    restrictions
                  </li>
                  <li>
                    <strong>Premium Subscriptions:</strong> Unlimited access to all features
                    including extended conversation sessions, advanced AI capabilities, and priority
                    support
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">Billing & Payment:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Recurring Charges:</strong> Subscription fees are charged automatically
                    in advance on a recurring basis (monthly or annual)
                  </li>
                  <li>
                    <strong>Payment Authorization:</strong> You authorize us to charge your selected
                    payment method for all applicable fees
                  </li>
                  <li>
                    <strong>Payment Processing:</strong> Payments are processed through secure
                    third-party payment processors
                  </li>
                  <li>
                    <strong>Price Changes:</strong> We may change subscription prices with at least
                    30 days advance notice
                  </li>
                  <li>
                    <strong>Failed Payments:</strong> If payment fails, we may suspend your
                    subscription and retry charging your payment method
                  </li>
                  <li>
                    <strong>Taxes:</strong> Prices may be subject to applicable taxes, which will be
                    added to your bill
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">Cancellation & Refunds:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Cancellation:</strong> You may cancel your subscription at any time
                    through your account settings or by contacting support
                  </li>
                  <li>
                    <strong>Effective Date:</strong> Cancellation takes effect at the end of your
                    current billing period. You retain access until then
                  </li>
                  <li>
                    <strong>No Partial Refunds:</strong> We do not provide pro-rated refunds for
                    partial billing periods
                  </li>
                  <li>
                    <strong>14-Day Right of Withdrawal:</strong> EU consumers have the right to
                    cancel within 14 days of purchase for a full refund, unless you have expressly
                    consented to begin using premium features during this period
                  </li>
                  <li>
                    <strong>Exceptional Refunds:</strong> Refunds outside the 14-day period may be
                    granted at our discretion for technical issues or service failures
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">Free Trial (if applicable):</p>
                <ul className="space-y-2">
                  <li>Free trials may be offered for limited periods</li>
                  <li>You must provide payment information to start a trial</li>
                  <li>You will be charged automatically when the trial ends unless you cancel</li>
                  <li>One free trial per user; additional trials may be restricted</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section id="limitations" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">7. AI Service Limitations</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You acknowledge and understand that:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li>Our AI tutor is an artificial intelligence system with inherent limitations</li>
              <li>Responses may occasionally be inaccurate, inappropriate, or biased</li>
              <li>The AI cannot replace human judgment or professional language instruction</li>
              <li>Service availability may be affected by technical issues or maintenance</li>
              <li>AI capabilities and accuracy may vary across different languages</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              We continuously work to improve our AI, but cannot guarantee perfect accuracy or
              availability.
            </p>
          </section>

          {/* Section 8 */}
          <section id="privacy" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">
              8. Data Collection & Storage
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Your privacy is important to us. Our processing of your personal data is governed by
              our{" "}
              <Link href="/privacy" className="text-orange-400 hover:text-orange-300">
                Privacy Policy
              </Link>{" "}
              and complies with applicable data protection regulations.
            </p>
            <div className="space-y-4 text-gray-300">
              <div>
                <p className="font-bold mb-2">Data We Collect:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Account Information:</strong> Email address, name, authentication
                    credentials, subscription status, and payment information
                  </li>
                  <li>
                    <strong>Conversation Data:</strong> Conversation transcripts and voice data
                    processed during speaking sessions. Pronunciation clips or user-created podcast
                    media may be stored when you use features that save those files
                  </li>
                  <li>
                    <strong>Learning Data:</strong> Progress metrics, performance analytics, lesson
                    history, vocabulary learned, and personalized learning preferences
                  </li>
                  <li>
                    <strong>Usage Data:</strong> Session duration, feature usage patterns,
                    interaction frequency, and engagement metrics
                  </li>
                  <li>
                    <strong>Technical Data:</strong> Device information, IP address, browser type,
                    operating system, and connection data
                  </li>
                  <li>
                    <strong>Location Data:</strong> General geographic location for pricing and
                    content localization purposes
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">How We Store Your Data:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Conversation Memory:</strong> All conversations are stored in our secure
                    database to enable personalized learning and continuity across sessions
                  </li>
                  <li>
                    <strong>Data Retention:</strong> Conversation data is retained for the duration
                    of your active account and for a reasonable period after account closure as
                    required by law
                  </li>
                  <li>
                    <strong>Security Measures:</strong> We employ industry-standard encryption,
                    access controls, and security protocols to protect your data
                  </li>
                  <li>
                    <strong>Data Location:</strong> Your data may be stored and processed in servers
                    located in various jurisdictions, with appropriate safeguards in place
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">Your Data Rights:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Access:</strong> Request a copy of all personal data we hold about you
                  </li>
                  <li>
                    <strong>Rectification:</strong> Correct inaccurate or incomplete data
                  </li>
                  <li>
                    <strong>Erasure:</strong> Request deletion of your conversation history and
                    personal data (subject to legal retention requirements)
                  </li>
                  <li>
                    <strong>Portability:</strong> Receive your data in a structured,
                    machine-readable format
                  </li>
                  <li>
                    <strong>Objection:</strong> Object to certain types of data processing
                  </li>
                  <li>
                    <strong>Restriction:</strong> Request limitation of data processing in certain
                    circumstances
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 9 */}
          <section id="voice" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">9. Voice Data Processing</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Our Service processes voice data to provide real-time language learning experiences.
              By using voice features, you consent to the following:
            </p>
            <div className="space-y-4 text-gray-300">
              <div>
                <p className="font-bold mb-2">Voice Recording & Processing:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Real-Time Processing:</strong> Your voice is captured, transmitted, and
                    processed in real-time through our AI systems to provide immediate feedback
                  </li>
                  <li>
                    <strong>Audio Storage:</strong> Voice recordings may be stored temporarily or
                    permanently to improve service quality, train our AI models, and maintain
                    conversation history
                  </li>
                  <li>
                    <strong>Transcription:</strong> Voice data is automatically transcribed into
                    text for analysis, storage, and display purposes
                  </li>
                  <li>
                    <strong>Quality Analysis:</strong> We analyze pronunciation, intonation, and
                    speech patterns to provide personalized feedback
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">Voice Data Usage:</p>
                <ul className="space-y-2">
                  <li>Providing real-time conversation and language tutoring</li>
                  <li>Generating personalized learning recommendations</li>
                  <li>Improving AI model accuracy and performance</li>
                  <li>Maintaining conversation continuity across sessions</li>
                  <li>Quality assurance and service improvement</li>
                </ul>
              </div>
              <p className="text-gray-300 leading-relaxed">
                <strong>Important:</strong> Voice recordings contain biometric information. You have
                the right to request deletion of your voice data at any time, subject to legal
                retention requirements.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section id="analytics" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">10. Analytics & Tracking</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We use analytics and tracking technologies to understand how users interact with our
              Service, improve user experience, and measure marketing effectiveness.
            </p>
            <div className="space-y-4 text-gray-300">
              <div>
                <p className="font-bold mb-2">Analytics Services:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Product Analytics:</strong> We use analytics platforms to track user
                    behavior, feature usage, session duration, and engagement metrics
                  </li>
                  <li>
                    <strong>Performance Monitoring:</strong> We collect technical performance data
                    including load times, error rates, and system stability metrics
                  </li>
                  <li>
                    <strong>A/B Testing:</strong> We may conduct experiments to test new features
                    and improvements, which may involve different user experiences
                  </li>
                  <li>
                    <strong>Conversion Tracking:</strong> We track user actions related to
                    subscriptions, upgrades, and feature adoption
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">Marketing & Attribution:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Advertising Analytics:</strong> We use tracking technologies to measure
                    the effectiveness of our marketing campaigns and advertising
                  </li>
                  <li>
                    <strong>Attribution Tracking:</strong> We track how users discover and engage
                    with our Service through various channels
                  </li>
                  <li>
                    <strong>Remarketing:</strong> We may use cookies and similar technologies to
                    show relevant advertisements to users who have visited our Service
                  </li>
                  <li>
                    <strong>Conversion Events:</strong> We track key actions such as sign-ups,
                    subscriptions, and feature usage for marketing optimization
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">Cookies & Similar Technologies:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Essential Cookies:</strong> Required for basic Service functionality,
                    authentication, and security
                  </li>
                  <li>
                    <strong>Analytics Cookies:</strong> Help us understand how users interact with
                    our Service
                  </li>
                  <li>
                    <strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and
                    measure campaign effectiveness
                  </li>
                  <li>
                    <strong>Preference Cookies:</strong> Remember your settings and preferences
                  </li>
                </ul>
              </div>
              <p className="text-gray-300 leading-relaxed">
                You can manage cookie preferences through your browser settings, but disabling
                certain cookies may limit Service functionality.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section id="third-party" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">11. Third-Party Services</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Our Service integrates with various third-party services to provide functionality,
              process payments, and improve user experience. These integrations may involve sharing
              your data with third parties.
            </p>
            <div className="space-y-4 text-gray-300">
              <div>
                <p className="font-bold mb-2">Service Providers We Use:</p>
                <ul className="space-y-2">
                  <li>
                    <strong>Authentication Services:</strong> Third-party authentication providers
                    for secure account access
                  </li>
                  <li>
                    <strong>Payment Processing:</strong> Payment processors and subscription
                    management platforms to handle billing and payments
                  </li>
                  <li>
                    <strong>Cloud Infrastructure:</strong> Cloud hosting and database services for
                    data storage and processing
                  </li>
                  <li>
                    <strong>AI & Machine Learning:</strong> Third-party AI services and APIs for
                    voice processing, transcription, and natural language understanding
                  </li>
                  <li>
                    <strong>Analytics Platforms:</strong> Analytics and monitoring services to track
                    usage and performance
                  </li>
                  <li>
                    <strong>Communication Services:</strong> Email and notification services for
                    account-related communications
                  </li>
                  <li>
                    <strong>Error Tracking:</strong> Error monitoring and debugging services to
                    identify and fix technical issues
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">Data Sharing:</p>
                <ul className="space-y-2">
                  <li>
                    We share only the minimum necessary data with third-party services to provide
                    functionality
                  </li>
                  <li>
                    Third-party services are contractually obligated to protect your data and use it
                    only for specified purposes
                  </li>
                  <li>
                    We conduct due diligence on third-party providers to ensure appropriate data
                    protection standards
                  </li>
                  <li>
                    Some third-party services may have their own terms and privacy policies that
                    govern their use of your data
                  </li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-2">International Data Transfers:</p>
                <p className="text-gray-300 leading-relaxed">
                  Some third-party services may be located outside your jurisdiction. When we
                  transfer data internationally, we ensure appropriate safeguards are in place, such
                  as Standard Contractual Clauses approved by the European Commission or other
                  legally recognized transfer mechanisms.
                </p>
              </div>
            </div>
          </section>

          {/* Section 12 */}
          <section id="conduct" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">12. User Conduct</h2>
            <p className="text-gray-300 leading-relaxed mb-4">You agree not to:</p>
            <ul className="space-y-2 text-gray-300">
              <li>Use the Service for illegal or harmful purposes</li>
              <li>Attempt to reverse engineer or exploit our AI technology</li>
              <li>Share inappropriate, offensive, or abusive content</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Create multiple accounts to circumvent limitations</li>
              <li>Use automated tools to access the Service</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              We reserve the right to monitor usage and take appropriate action against violations.
            </p>
          </section>

          {/* Section 13 */}
          <section id="ip" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">13. Intellectual Property</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                <strong>Our Rights:</strong> Chicky, our AI technology, software, and related
                content are protected by intellectual property laws. All rights are reserved.
              </p>
              <p>
                <strong>Your Content:</strong> You retain ownership of content you create using our
                Service, but grant us a license to use it for service provision and improvement.
              </p>
              <p>
                <strong>AI-Generated Content:</strong> Content generated by our AI during your
                sessions may be used to improve our Service, subject to privacy protections.
              </p>
            </div>
          </section>

          {/* Section 14 */}
          <section id="disclaimers" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">14. Disclaimers</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL
              WARRANTIES, EXPRESS OR IMPLIED, INCLUDING:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li>Accuracy or completeness of AI responses</li>
              <li>Uninterrupted or error-free operation</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              This disclaimer is subject to applicable consumer protection laws in your
              jurisdiction.
            </p>
          </section>

          {/* Section 15 */}
          <section id="liability" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">15. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              To the maximum extent permitted by law, our liability is limited to:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li>The amount you paid for the Service in the 12 months preceding the claim</li>
              <li>Direct damages only (excluding indirect, consequential, or punitive damages)</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4 mb-2">
              This limitation does not apply to:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li>Death or personal injury caused by our negligence</li>
              <li>Fraudulent misrepresentation</li>
              <li>Violations of your data protection rights</li>
              <li>Other matters where liability cannot be excluded under applicable law</li>
            </ul>
          </section>

          {/* Section 16 */}
          <section id="termination" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">16. Termination</h2>
            <div className="space-y-4 text-gray-300">
              <p>
                <strong>By You:</strong> You may terminate your account at any time through your
                account settings.
              </p>
              <p>
                <strong>By Us:</strong> We may suspend or terminate your access if you violate these
                Terms or for other legitimate reasons with appropriate notice.
              </p>
              <p>
                <strong>Effect of Termination:</strong> Upon termination, your access ends, but
                certain provisions of these Terms survive, including intellectual property rights
                and limitation of liability.
              </p>
            </div>
          </section>

          {/* Section 17 */}
          <section id="governing" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">17. Governing Law</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              These Terms are governed by applicable local laws and regulations. Any disputes will
              be resolved through appropriate legal channels in accordance with your local
              jurisdiction.
            </p>
            <p className="text-gray-300 leading-relaxed">
              If you are an EU consumer, you may also use the EU Online Dispute Resolution platform
              at{" "}
              <a
                href="https://ec.europa.eu/consumers/odr"
                className="text-orange-400 hover:text-orange-300 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                ec.europa.eu/consumers/odr
              </a>
              .
            </p>
          </section>

          {/* Section 18 */}
          <section id="changes" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">18. Changes to Terms</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We may update these Terms to reflect changes in our Service or legal requirements. We
              will notify you of material changes by:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li>Email notification to your registered address</li>
              <li>Prominent notice in our Service</li>
              <li>At least 30 days advance notice for material changes</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              Continued use after changes take effect constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Section 19 */}
          <section id="contact" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-orange-400">19. Contact Information</h2>
            <p className="text-gray-300 leading-relaxed">
              For questions about these Terms or our Service, email{" "}
              <a
                href="mailto:feedback@chickytutor.com"
                className="text-orange-400 hover:text-orange-300"
              >
                feedback@chickytutor.com
              </a>
              . Privacy and account-deletion requests can also be submitted through our{" "}
              <Link href="/delete-account" className="text-orange-400 hover:text-orange-300">
                account deletion page
              </Link>
              .
            </p>
          </section>
        </motion.div>

        {/* Back to Top */}
        <div className="mt-12 text-center">
          <a
            href="#"
            className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
          >
            Back to Top
          </a>
        </div>
      </div>
    </div>
  );
}
